import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  Users,
  ShoppingCart,
  DollarSign,
  RefreshCw,
  Wallet,
  Clock,
  Activity,
} from 'lucide-react';
import api from '../utils/api';

function StatCard({ icon: Icon, label, value, subValue, color = 'blue', loading }) {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    purple: 'bg-purple-500/20 text-purple-400',
    amber: 'bg-amber-500/20 text-amber-400',
    rose: 'bg-rose-500/20 text-rose-400',
    cyan: 'bg-cyan-500/20 text-cyan-400',
  };

  return (
    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon size={20} />
        </div>
        <span className="text-xs text-slate-400 font-medium">{label}</span>
      </div>
      {loading ? (
        <div className="h-8 bg-slate-700 rounded animate-pulse" />
      ) : (
        <>
          <p className="text-2xl font-bold text-white">{value ?? '—'}</p>
          {subValue && <p className="text-xs text-slate-500 mt-1">{subValue}</p>}
        </>
      )}
    </div>
  );
}

function MiniChart({ data, label }) {
  if (!data || data.length === 0) return null;

  const max = Math.max(...data.map(d => d.value), 1);
  const barWidth = Math.max(100 / data.length - 2, 4);

  return (
    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
      <p className="text-xs text-slate-400 font-medium mb-3">{label}</p>
      <div className="flex items-end gap-1 h-24">
        {data.map((d, i) => (
          <div key={i} className="flex flex-col items-center flex-1">
            <div
              className="w-full bg-blue-500/60 rounded-t transition-all duration-300 min-h-[2px]"
              style={{
                height: `${(d.value / max) * 100}%`,
                maxWidth: `${barWidth}%`,
              }}
              title={`${d.label}: ${d.value}`}
            />
            {data.length <= 14 && (
              <span className="text-[10px] text-slate-600 mt-1 truncate w-full text-center">
                {d.label}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardScreen() {
  const [stats, setStats] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [rateSettings, setRateSettings] = useState(null);
  const [altynBalance, setAltynBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('day');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        api.getStats(period),
        api.getDailyStats(7),
        api.getRealtime(),
        api.getDashboard(30),
        api.getRateSettings(),
        api.getAltynBalance(),
      ]);

      const [statsRes, dailyRes, realtimeRes, dashRes, ratesRes, altynRes] = results;

      if (statsRes.status === 'fulfilled') setStats(statsRes.value);
      if (dailyRes.status === 'fulfilled') setDailyStats(dailyRes.value);
      if (realtimeRes.status === 'fulfilled') setRealtime(realtimeRes.value);
      if (dashRes.status === 'fulfilled') setDashboard(dashRes.value);
      if (ratesRes.status === 'fulfilled') setRateSettings(ratesRes.value);
      if (altynRes.status === 'fulfilled') setAltynBalance(altynRes.value);

      // Check if all failed
      if (results.every(r => r.status === 'rejected')) {
        throw results[0].reason;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh realtime every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const data = await api.getRealtime();
        setRealtime(data);
      } catch { /* ignore */ }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '—';
    if (typeof num !== 'number') num = Number(num);
    if (isNaN(num)) return '—';
    return num.toLocaleString('ru-RU');
  };

  const formatCurrency = (num, currency = '') => {
    if (num === null || num === undefined) return '—';
    return `${formatNumber(num)} ${currency}`.trim();
  };

  // Prepare daily chart data
  const dailyChartData = dailyStats?.daily?.map(d => ({
    label: d.date?.slice(5) || '',
    value: d.orders || d.count || 0,
  })) || [];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Дашборд</h2>
        <div className="flex items-center gap-2">
          {/* Period selector */}
          <div className="flex bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
            {['day', 'week', 'month'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  period === p
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {{ day: 'День', week: 'Неделя', month: 'Месяц' }[p]}
              </button>
            ))}
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="p-2 bg-slate-800 rounded-lg border border-slate-700 text-slate-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Realtime banner */}
      {realtime && (
        <div className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 rounded-xl p-4 mb-6 border border-blue-800/30">
          <div className="flex items-center gap-2 mb-2">
            <Activity size={14} className="text-green-400" />
            <span className="text-xs text-green-400 font-medium">Реалтайм</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-slate-400">Активные за 5 мин</p>
              <p className="text-lg font-bold text-white">{realtime.activeUsers ?? realtime.active5min ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Запросов/час</p>
              <p className="text-lg font-bold text-white">{realtime.requestsPerHour ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Заблок. IP</p>
              <p className="text-lg font-bold text-white">{realtime.blockedIPs ?? realtime.blockedIps ?? '—'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
        <StatCard
          icon={ShoppingCart}
          label="Ордера"
          value={formatNumber(stats?.orders?.total ?? stats?.orders)}
          subValue={stats?.orders?.completed ? `Завершено: ${stats.orders.completed}` : undefined}
          color="blue"
          loading={loading}
        />
        <StatCard
          icon={DollarSign}
          label="Объём CNY"
          value={formatCurrency(stats?.volume?.totalCNY ?? stats?.volumeCNY, '¥')}
          color="green"
          loading={loading}
        />
        <StatCard
          icon={TrendingUp}
          label="Объём RUB"
          value={formatCurrency(stats?.volume?.totalRUB ?? stats?.volumeRUB, '₽')}
          color="purple"
          loading={loading}
        />
        <StatCard
          icon={DollarSign}
          label="Прибыль"
          value={formatCurrency(stats?.profit?.estimatedRUB ?? stats?.profit, '₽')}
          subValue={stats?.profit?.marginPercent ? `Маржа: ${stats.profit.marginPercent}%` : undefined}
          color="amber"
          loading={loading}
        />
        <StatCard
          icon={Users}
          label="Пользователи"
          value={formatNumber(stats?.users?.newFromStart ?? stats?.users)}
          subValue={stats?.users?.appOpens ? `Открытий: ${stats.users.appOpens}` : undefined}
          color="cyan"
          loading={loading}
        />
        <StatCard
          icon={Clock}
          label="KYC"
          value={formatNumber(stats?.kyc?.verified ?? stats?.kycVerified)}
          subValue={stats?.kyc?.rejected ? `Отклонено: ${stats.kyc.rejected}` : undefined}
          color="rose"
          loading={loading}
        />
        <StatCard
          icon={Wallet}
          label="Баланс Altyn"
          value={formatCurrency(stats?.altynBalance?.balance ?? altynBalance?.balance, stats?.altynBalance?.currency || 'USDT')}
          color="green"
          loading={loading}
        />
        <StatCard
          icon={TrendingUp}
          label="Курс RUB/CNY"
          value={rateSettings?.liveRate ?? rateSettings?.rate ?? '—'}
          subValue={rateSettings?.marginPercent ? `Маржа: ${rateSettings.marginPercent}%` : undefined}
          color="blue"
          loading={loading}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <MiniChart data={dailyChartData} label="Ордера по дням (7 дней)" />

        {/* Top events from dashboard */}
        {dashboard?.topEvents && (
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <p className="text-xs text-slate-400 font-medium mb-3">Top события (30 дней)</p>
            <div className="space-y-2">
              {dashboard.topEvents.slice(0, 6).map((event, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-slate-300 truncate">{event.type || event.name}</span>
                  <span className="text-sm font-medium text-white">{formatNumber(event.count)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Daily breakdown table */}
      {dailyStats?.daily && dailyStats.daily.length > 0 && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <h3 className="text-sm font-medium text-white">Разбивка по дням</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 text-xs">
                  <th className="text-left p-3">Дата</th>
                  <th className="text-right p-3">Ордера</th>
                  <th className="text-right p-3">CNY</th>
                  <th className="text-right p-3">RUB</th>
                  <th className="text-right p-3">Новых</th>
                  <th className="text-right p-3">Прибыль</th>
                </tr>
              </thead>
              <tbody>
                {dailyStats.daily.map((day, i) => (
                  <tr key={i} className="border-t border-slate-700/50 hover:bg-slate-700/30">
                    <td className="p-3 text-slate-300">{day.date}</td>
                    <td className="p-3 text-right text-white">{formatNumber(day.orders ?? day.count)}</td>
                    <td className="p-3 text-right text-white">{formatCurrency(day.volumeCNY ?? day.cny)}</td>
                    <td className="p-3 text-right text-white">{formatCurrency(day.volumeRUB ?? day.rub)}</td>
                    <td className="p-3 text-right text-slate-400">{day.newUsers ?? '—'}</td>
                    <td className="p-3 text-right text-green-400">{formatCurrency(day.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
